<template>
  <b-card no-body class="border mt-1 mb-2">
    <div class="d-flex align-items-center justify-content-between p-1 border-bottom">
      <div style="min-width: 0;">
        <div class="d-flex align-items-center" style="min-width: 0;">
          <h5 class="mb-0 text-truncate">{{ albumName }}</h5>
          <!-- Ссылка на альбом в источнике (Спотифай / Яндекс.Музыка). -->
          <b-button
              v-if="sourceUrl"
              :href="sourceUrl"
              target="_blank"
              :variant="source === 'yandex' ? undefined : 'success'"
              :class="['btn-icon ml-50 flex-shrink-0', { 'btn-yandex': source === 'yandex' }]"
              size="sm"
              v-b-tooltip.hover
              :title="source === 'yandex' ? 'Открыть в Яндекс.Музыке' : 'Открыть в Спотифай'"
          >
            <feather-icon :icon="source === 'yandex' ? 'PlayCircleIcon' : 'MusicIcon'" size="14" />
          </b-button>
        </div>
        <!-- Артисты альбома: в БД — ссылка, нет — поиск. -->
        <div v-if="artists && artists.length" class="font-small-2 mt-25">
          <span v-for="(artist, artistIndex) in artists" :key="artistIndex">
            <b-link
                v-if="artist.id && $can('view', 'artist')"
                :to="{ name: 'artist-view', params: { id: artist.id } }"
                class="font-weight-bold text-primary"
            >{{ artist.name }}</b-link>
            <!-- Артист в БД есть, но карточка недоступна: имя текстом, без поиска -->
            <strong v-else-if="artist.id" class="text-primary">{{ artist.name }}</strong>
            <template v-else>
              {{ artist.name }}
              <b-button
                  :to="{ name: 'artists-search', query: { q: artist.name } }"
                  variant="info"
                  class="btn-icon ml-25"
                  size="sm"
                  v-b-tooltip.hover
                  title="Найти в Last.FM"
              >
                <feather-icon icon="SearchIcon" size="12" />
              </b-button>
            </template>
            <span v-if="artistIndex < artists.length - 1">, </span>
          </span>
        </div>
      </div>
      <b-button
          variant="flat-secondary"
          class="btn-icon"
          size="sm"
          v-b-tooltip.hover
          title="Закрыть"
          @click="$emit('close')"
      >
        <feather-icon icon="XIcon" />
      </b-button>
    </div>

    <div class="p-1">
      <div v-if="loading" class="text-muted">
        <b-spinner small class="mr-50" /> Загрузка...
      </div>
      <div v-else>
        <div
            v-for="track in tracks"
            :key="track.index"
            class="d-flex align-items-center py-50 border-bottom"
            style="gap: 0.75rem;"
        >
          <span class="text-muted" style="width: 2rem; text-align: right;">{{ track.index }}.</span>
          <div class="flex-grow-1" style="min-width: 0;">
            <div class="text-truncate">
              <b-link
                  v-if="track.track_id && $can('view', 'track-view')"
                  :to="{ name: 'track-detail', params: { id: track.track_id } }"
              >{{ track.title }}</b-link>
              <template v-else>{{ track.title }}</template>
            </div>
            <!-- В строках треков артисты — просто текст; ссылки/кнопки живут
                 на карточке альбома и в шапке панели. -->
            <div
                v-if="hasArtistList(track) && track.artists.length"
                class="font-small-2 text-primary text-truncate"
            >{{ artistNames(track) }}</div>
            <div
                v-else-if="!hasArtistList(track) && track.artists"
                class="font-small-2 text-primary text-truncate"
            >{{ track.artists }}</div>
          </div>
          <b-form-rating
              v-if="track.popularity != null"
              :value="toStarRating(track.popularity)"
              :stars="5"
              readonly
              inline
              no-border
              half
              size="sm"
              variant="warning"
              class="p-0"
              style="width: auto;"
              v-b-tooltip.hover
              :title="'Популярность: ' + track.popularity"
          />
          <span class="text-muted font-small-2" style="width: 3rem; text-align: right;">{{ formatDuration(track.duration_ms) }}</span>
          <feather-icon
              v-if="track.in_db"
              icon="CheckIcon"
              class="text-success"
              size="20"
              v-b-tooltip.hover
              title="Есть в базе"
          />
          <b-button
              v-else
              :variant="addTrackVariant(track)"
              class="btn-icon"
              size="sm"
              :disabled="track.adding"
              v-b-tooltip.hover
              title="Добавить трек"
              @click="$emit('add-track', track)"
          >
            <b-spinner v-if="track.adding" small />
            <feather-icon v-else icon="PlusIcon" size="16" />
          </b-button>
        </div>
        <div v-if="tracks && !tracks.length" class="text-muted">Треки не найдены</div>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BCard, BButton, BSpinner, BFormRating, BLink, VBTooltip } from 'bootstrap-vue'

export default {
  name: 'AlbumTracksPanel',
  components: { BCard, BButton, BSpinner, BFormRating, BLink },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    albumName: { type: String, default: '' },
    // Артисты альбома [{name, id|null}] — только на странице релизов.
    artists: { type: Array, default: null },
    // Источник альбома ('spotify' | 'yandex') и ссылка на него — для иконки в шапке.
    source: { type: String, default: null },
    sourceUrl: { type: String, default: null },
    tracks: { type: Array, default: null },
    loading: { type: Boolean, default: false },
  },
  methods: {
    toStarRating(popularity) {
      return Math.round(popularity / 10) / 2
    },
    formatDuration(ms) {
      if (!ms) return ''
      const totalSeconds = Math.round(ms / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    // Страница релизов передаёт артистов массивом объектов {name, id},
    // страница артиста — строкой. Различаем форму, а не источник.
    hasArtistList(track) {
      return Array.isArray(track.artists)
    },
    artistNames(track) {
      return track.artists.map(artist => artist.name).join(', ')
    },
    // Зелёная — первый артист уже в БД (трек привяжется к нему),
    // жёлтая — артиста нет, добавление сначала создаст артиста.
    addTrackVariant(track) {
      if (!this.hasArtistList(track)) return 'outline-success'
      const firstArtist = track.artists[0]
      return firstArtist && firstArtist.id ? 'outline-success' : 'outline-warning'
    },
  },
}
</script>
